/**
 * Image URL helper — rewrites partner-CDN URLs through our own /api/img
 * proxy so visitors get resized + WebP'd photos instead of the 200 KB+
 * originals served by Nelemans.
 *
 * Why a small helper instead of inline rewrites:
 *   - Single place to control which widths we request → matches the
 *     backend's allowed-width bucket so we hit cache.
 *   - Skip the rewrite for non-allowlisted hosts (e.g. an image that's
 *     already on a fast CDN like Unsplash full-size) so we don't proxy
 *     unnecessarily.
 *   - Falls back to the raw URL if REACT_APP_BACKEND_URL is unavailable.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROXIED_HOSTS = ['nelemans.es', 'www.nelemans.es'];

const ALLOWED_WIDTHS = [200, 400, 600, 900, 1200, 1600];
const snap = (w) => ALLOWED_WIDTHS.reduce(
  (best, cur) => (Math.abs(cur - w) < Math.abs(best - w) ? cur : best),
  ALLOWED_WIDTHS[0]
);

export const proxiedImage = (rawUrl, width = 800) => {
  if (!rawUrl) return '';
  try {
    const u = new URL(rawUrl);
    if (!PROXIED_HOSTS.includes(u.host)) return rawUrl;
    const w = snap(width);
    return `${API_URL}/api/img?url=${encodeURIComponent(rawUrl)}&w=${w}`;
  } catch {
    return rawUrl;
  }
};

/** Build a `srcset` string for responsive images at multiple widths. */
export const proxiedSrcSet = (rawUrl, widths = [400, 600, 900]) =>
  widths
    .filter((w) => ALLOWED_WIDTHS.includes(w))
    .map((w) => `${proxiedImage(rawUrl, w)} ${w}w`)
    .join(', ');
